import { useCallback, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/toast';
import { useAccessToken } from '@/services/auth';
import { OnFarmImplantRequestError } from '@/services/on-farm-implant';

import type { MatingRecord } from '../../types';
import {
  matingEmbryosOptions,
  toEmbryoRecordKey,
  useClientOnFarmSelectionContext,
} from './clientOnFarmSelectionContext';

export const ClientOnFarmMatingCheckbox = ({
  data,
}: {
  data?: MatingRecord;
}) => {
  const { t } = useTranslation();
  const {
    selectedKeys,
    visibleKeys,
    search,
    readOnly,
    setEmbryosSelected,
    loadMatingEmbryoKeys,
  } = useClientOnFarmSelectionContext();
  const [isLoading, setIsLoading] = useState(false);

  const token = useAccessToken();
  const { data: embryos } = useQuery({
    ...matingEmbryosOptions(
      token,
      { id: data?.id ?? '', accountId: data?.accountId ?? null },
      search,
    ),
    enabled: !!token && !!data?.id,
  });

  const embryoKeys = useMemo(() => {
    const keys = (embryos ?? []).map(toEmbryoRecordKey);
    return visibleKeys ? keys.filter((key) => visibleKeys.has(key)) : keys;
  }, [embryos, visibleKeys]);
  const selectedCount = embryoKeys.filter((key) =>
    selectedKeys.has(key),
  ).length;
  const checked = embryoKeys.length > 0 && selectedCount === embryoKeys.length;
  const indeterminate = selectedCount > 0 && !checked;

  const handleCheckedChange = useCallback(
    async (next: boolean) => {
      if (!data) return;
      setIsLoading(true);
      try {
        setEmbryosSelected(await loadMatingEmbryoKeys(data), next);
      } catch (error) {
        const detail =
          error instanceof OnFarmImplantRequestError && error.problems.length
            ? error.problems.join(' ')
            : undefined;
        toast.add({
          data: { variant: 'error' },
          title: t('onFarmImplant.sourceCard.loadEmbryosError'),
          description: detail,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [data, loadMatingEmbryoKeys, setEmbryosSelected, t],
  );

  if (!data) return null;

  return (
    <div className="flex h-full items-center">
      <Checkbox
        aria-label={t('onFarmImplant.sourceCard.selectMatingEmbryos', {
          mating: data.recordName ?? data.id,
        })}
        checked={checked}
        indeterminate={indeterminate}
        disabled={readOnly || isLoading}
        onCheckedChange={(next) => {
          void handleCheckedChange(next);
        }}
      />
    </div>
  );
};
